import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { createEmployeeNavSections } from '../components/layout/dashboardNavConfigs';
import { timeOffRequestsAPI, timeEntriesAPI, tasksAPI } from '../services/api';
import { FilterIcon } from '../components/icons';

interface TimeOffEntry {
  id: number;
  start_date: string;
  end_date: string;
  request_type: string;
  status: string;
  user?: { id?: number; full_name?: string };
  user_id?: number;
}

interface TimeEntryItem {
  id: number;
  entry_date: string;
  hours_worked: number;
  description?: string;
  project?: { id: number; name: string };
  user?: { id: number };
  user_id?: number;
}

interface TaskItem {
  id: number;
  title: string;
  due_date?: string;
  status: string;
  priority?: string;
  assigned_to_user_id?: number;
  assigned_to?: { id?: number; name?: string };
  project?: { id: number; name: string };
}

const monthNamesEn = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const monthNamesFr = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

type PillItem =
  | { type: 'timeoff'; data: TimeOffEntry }
  | { type: 'entry'; data: TimeEntryItem }
  | { type: 'task'; data: TaskItem };

const pad = (n: number) => String(n).padStart(2, '0');
const dateKey = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const roundHours = (hours: number) => Math.round(hours * 100) / 100;

const MyCalendar: React.FC = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { showToast } = useToast();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [timeOff, setTimeOff] = useState<TimeOffEntry[]>([]);
  const [entries, setEntries] = useState<TimeEntryItem[]>([]);
  const [tasks, setTasks] = useState<TaskItem[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    () => {
      const show = searchParams.get('show');
      return show
        ? new Set(show.split(','))
        : new Set(['timeoff', 'entries', 'tasks']);
    },
  );

  const [taskStatusFilters, setTaskStatusFilters] = useState<Set<string>>(
    () => {
      const statuses = searchParams.get('taskStatus');
      return statuses
        ? new Set(statuses.split(','))
        : new Set(['todo', 'in_progress', 'pending', 'on_hold']);
    },
  );

  const [timeoffStatusFilters, setTimeoffStatusFilters] = useState<Set<string>>(
    () => {
      const statuses = searchParams.get('timeoffStatus');
      return statuses
        ? new Set(statuses.split(','))
        : new Set(['pending', 'approved']);
    },
  );

  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);

  const updateFilters = useCallback(() => {
    const params = new URLSearchParams();

    // Only add params if they differ from defaults
    if (visibleCategories.size < 3) {
      params.set('show', Array.from(visibleCategories).join(','));
    }
    if (taskStatusFilters.size < 4) {
      params.set('taskStatus', Array.from(taskStatusFilters).join(','));
    }
    if (timeoffStatusFilters.size < 2) {
      params.set('timeoffStatus', Array.from(timeoffStatusFilters).join(','));
    }

    // Write params to URL, or clear if all defaults
    setSearchParams(params.toString() ? params : {});
  }, [
    visibleCategories,
    taskStatusFilters,
    timeoffStatusFilters,
    setSearchParams,
  ]);

  useEffect(() => {
    updateFilters();
  }, [
    visibleCategories,
    taskStatusFilters,
    timeoffStatusFilters,
    updateFilters,
  ]);

  const isActive = (href: string) => {
    if (href === '/calendar') return location.pathname === '/calendar';
    return location.pathname.startsWith(href);
  };
  const sections = createEmployeeNavSections(t, isActive);

  const monthNames = i18n.language?.startsWith('fr')
    ? monthNamesFr
    : monthNamesEn;
  const weekdayLabels = i18n.language?.startsWith('fr')
    ? ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const monthStart = useMemo(() => dateKey(year, month, 1), [year, month]);
  const monthEnd = useMemo(
    () => dateKey(year, month, new Date(year, month, 0).getDate()),
    [year, month],
  );

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [tOffRes, entriesRes, tasksRes] = await Promise.all([
          timeOffRequestsAPI.calendar(year, month),
          timeEntriesAPI.getAll({
            user_id: user.id,
            start_date: monthStart,
            end_date: monthEnd,
          }),
          tasksAPI.getAll({ assigned_to_user_id: user.id }),
        ]);
        if (cancelled) return;

        const tOffAll: TimeOffEntry[] = tOffRes.data.calendar_entries || [];
        const tOffMine = tOffAll.filter(
          (e) => e.user?.id === user.id || e.user_id === user.id,
        );

        const entriesRaw: TimeEntryItem[] =
          entriesRes.data.time_entries || entriesRes.data || [];
        // Backend filters by user_id query param; trust that and only keep
        // entries with a matching user when the field is present.
        const entriesMine = entriesRaw.filter((e) =>
          !e.user_id && !e.user
            ? true
            : e.user?.id === user.id || e.user_id === user.id,
        );

        const tasksRaw: TaskItem[] = tasksRes.data.tasks || tasksRes.data || [];
        const tasksMine = tasksRaw.filter(
          (t2) =>
            !!t2.due_date &&
            (t2.assigned_to_user_id === user.id ||
              t2.assigned_to?.id === user.id),
        );

        setTimeOff(tOffMine);
        setEntries(entriesMine);
        setTasks(tasksMine);
      } catch (err) {
        if (!cancelled)
          showToast(
            t('calendar.loadError') || 'Failed to load calendar',
            'error',
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id, year, month, monthStart, monthEnd, showToast, t]);

  const prevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else setMonth(month + 1);
  };
  const goToday = () => {
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
  };

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = new Date(year, month - 1, 1).getDay();

  const timeOffColor: Record<string, string> = isDark
    ? {
        vacation: 'bg-indigo-500/30 text-indigo-300',
        sick: 'bg-red-500/30 text-red-300',
        sick_leave: 'bg-red-500/30 text-red-300',
        personal: 'bg-blue-500/30 text-blue-300',
        emergency: 'bg-amber-500/30 text-amber-300',
      }
    : {
        vacation: 'bg-indigo-100 text-indigo-700',
        sick: 'bg-red-100 text-red-700',
        sick_leave: 'bg-red-100 text-red-700',
        personal: 'bg-blue-100 text-blue-700',
        emergency: 'bg-amber-100 text-amber-700',
      };

  const priorityColor = (p?: string) => {
    const map: Record<string, string> = isDark
      ? {
          urgent: 'bg-red-500/30 text-red-300',
          high: 'bg-orange-500/30 text-orange-300',
          medium: 'bg-yellow-500/30 text-yellow-300',
          low: 'bg-slate-600 text-slate-300',
        }
      : {
          urgent: 'bg-red-100 text-red-700',
          high: 'bg-orange-100 text-orange-700',
          medium: 'bg-yellow-100 text-yellow-800',
          low: 'bg-slate-100 text-slate-600',
        };
    return map[p || 'low'] || map.low;
  };

  const getDayData = (
    day: number,
    visibleCategories: Set<string>,
    taskStatusFilters: Set<string>,
    timeoffStatusFilters: Set<string>,
  ) => {
    const key = dateKey(year, month, day);
    const tOff = timeOff.filter(
      (e) => e.start_date <= key && e.end_date >= key,
    );
    const ents = entries.filter((e) => e.entry_date === key);
    const tks = tasks.filter(
      (t2) => t2.due_date && t2.due_date.startsWith(key),
    );

    // Apply visibility and status filters
    const filteredTimeOff = visibleCategories.has('timeoff')
      ? tOff.filter((item) => timeoffStatusFilters.has(item.status))
      : [];

    const filteredEntries = visibleCategories.has('entries') ? ents : [];

    const filteredTasks = visibleCategories.has('tasks')
      ? tks.filter((item) => taskStatusFilters.has(item.status))
      : [];

    // Return filtered data structure
    return {
      tOff: filteredTimeOff,
      ents: filteredEntries,
      tks: filteredTasks,
      totalHours: filteredEntries.reduce(
        (sum, e) => sum + (Number(e.hours_worked) || 0),
        0,
      ),
    };
  };

  const cardClass = isDark
    ? 'bg-slate-900/50 border-slate-700/50 border'
    : 'bg-white border-slate-200 border';

  const content = (
    <>
      {/* Sticky Header */}
      <div
        className={`border-b ${isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-white/80 border-slate-200'} backdrop-blur-sm sticky top-0 z-10`}
      >
        <div className="px-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 py-5">
            <div>
              <h1
                className={`text-xl sm:text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {t('calendar.myCalendar') || 'My Calendar'}
              </h1>
              <p
                className={`mt-0.5 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('calendar.subtitle') ||
                  'Time off, time entries, and task deadlines in one view'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={prevMonth}
                className={`p-2 rounded-xl transition ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}
                aria-label="Previous month"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                onClick={goToday}
                className={`px-3 py-1.5 text-sm font-medium rounded-xl transition ${isDark ? 'bg-slate-800 text-slate-200 hover:bg-slate-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              >
                {t('calendar.today') || 'Today'}
              </button>
              <div
                className={`min-w-[160px] text-center text-base font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {monthNames[month - 1]} {year}
              </div>
              <button
                onClick={nextMonth}
                className={`p-2 rounded-xl transition ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}
                aria-label="Next month"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 mb-4 text-xs">
          <LegendDot
            color={isDark ? 'bg-indigo-500/60' : 'bg-indigo-400'}
            label={t('calendar.legendTimeOff') || 'Time off'}
            isDark={isDark}
            isActive={visibleCategories.has('timeoff')}
            onClick={() => {
              const newSet = new Set(visibleCategories);
              if (newSet.has('timeoff')) newSet.delete('timeoff');
              else newSet.add('timeoff');
              setVisibleCategories(newSet);
            }}
          />
          <LegendDot
            color={isDark ? 'bg-emerald-500/60' : 'bg-emerald-400'}
            label={t('calendar.legendTimeEntries') || 'Hours logged'}
            isDark={isDark}
            isActive={visibleCategories.has('entries')}
            onClick={() => {
              const newSet = new Set(visibleCategories);
              if (newSet.has('entries')) newSet.delete('entries');
              else newSet.add('entries');
              setVisibleCategories(newSet);
            }}
          />
          <LegendDot
            color={isDark ? 'bg-violet-500/60' : 'bg-violet-400'}
            label={t('calendar.legendTasks') || 'Task due'}
            isDark={isDark}
            isActive={visibleCategories.has('tasks')}
            onClick={() => {
              const newSet = new Set(visibleCategories);
              if (newSet.has('tasks')) newSet.delete('tasks');
              else newSet.add('tasks');
              setVisibleCategories(newSet);
            }}
          />
          <span
            className={`ml-auto ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
          >
            {t('calendar.pendingHint') || 'Yellow border = pending'}
          </span>
        </div>

        {/* Mobile filter toggle button */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="btn btn-secondary flex items-center gap-2 w-full justify-center"
          >
            <FilterIcon className="w-4 h-4" />
            {t('calendar.filterShow')}
            {(visibleCategories.size < 3 ||
              taskStatusFilters.size < 4 ||
              timeoffStatusFilters.size < 2) && (
              <span className="inline-block bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded">
                {3 -
                  visibleCategories.size +
                  (4 - taskStatusFilters.size) +
                  (2 - timeoffStatusFilters.size)}
              </span>
            )}
          </button>
        </div>

        {/* Desktop filter panel - always visible on lg+ */}
        <div className="hidden lg:block mb-6 p-4 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-900">
          <div
            className={`flex items-center gap-2 mb-3 text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}
          >
            <FilterIcon className="w-4 h-4" />
            {t('calendar.filterShow')}
          </div>
          <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
            {/* Task Status Filter - Toggle Chips */}
            <div>
              <label
                className={`block text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}
              >
                {t('calendar.filterTaskStatus')}
              </label>
              <p
                className={`text-xs mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
              >
                {t('calendar.filterTaskStatusHint')}
              </p>
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    ['todo', 'taskStatusTodo'],
                    ['in_progress', 'taskStatusInProgress'],
                    ['pending', 'taskStatusPending'],
                    ['on_hold', 'taskStatusOnHold'],
                  ] as const
                ).map(([value, labelKey]) => (
                  <button
                    key={value}
                    onClick={() => {
                      const newSet = new Set(taskStatusFilters);
                      if (newSet.has(value)) newSet.delete(value);
                      else newSet.add(value);
                      setTaskStatusFilters(
                        newSet.size > 0
                          ? newSet
                          : new Set(['todo', 'in_progress', 'pending', 'on_hold']),
                      );
                    }}
                    className={`px-3 py-1 rounded text-sm font-semibold transition ${
                      taskStatusFilters.has(value)
                        ? 'bg-blue-600 text-white'
                        : isDark
                          ? 'bg-slate-700 text-white'
                          : 'bg-slate-200 text-slate-900'
                    }`}
                  >
                    {t(`calendar.${labelKey}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Time-off Status Filter - Toggle Chips */}
            <div>
              <label
                className={`block text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}
              >
                {t('calendar.filterTimeOffStatus')}
              </label>
              <p
                className={`text-xs mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
              >
                {t('calendar.filterTimeOffStatusHint')}
              </p>
              <div className="flex gap-2">
                {(['pending', 'approved'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => {
                      const newSet = new Set(timeoffStatusFilters);
                      if (newSet.has(status)) newSet.delete(status);
                      else newSet.add(status);
                      setTimeoffStatusFilters(newSet);
                    }}
                    className={`px-3 py-1 rounded text-sm font-semibold transition ${
                      timeoffStatusFilters.has(status)
                        ? 'bg-blue-600 text-white'
                        : isDark
                          ? 'bg-slate-700 text-white'
                          : 'bg-slate-200 text-slate-900'
                    }`}
                  >
                    {status === 'pending'
                      ? t('calendar.filterPending')
                      : t('calendar.filterApproved')}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filters Button */}
            <button
              onClick={() => {
                setVisibleCategories(new Set(['timeoff', 'entries', 'tasks']));
                setTaskStatusFilters(
                  new Set(['todo', 'in_progress', 'pending', 'on_hold']),
                );
                setTimeoffStatusFilters(new Set(['pending', 'approved']));
                setSearchParams({});
              }}
              className="btn btn-secondary ml-auto mt-5"
            >
              {t('calendar.filterClear')}
            </button>
          </div>
        </div>

        {/* Mobile filter panel - visible when filtersOpen is true */}
        {filtersOpen && (
          <div className="lg:hidden mb-6 p-4 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-900">
            <div className="space-y-4">
              {/* Task Status - Toggle Chips */}
              <div>
                <label
                  className={`block text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}
                >
                  {t('calendar.filterTaskStatus')}
                </label>
                <p
                  className={`text-xs mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                >
                  {t('calendar.filterTaskStatusHint')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {(
                    [
                      ['todo', 'taskStatusTodo'],
                      ['in_progress', 'taskStatusInProgress'],
                      ['pending', 'taskStatusPending'],
                      ['on_hold', 'taskStatusOnHold'],
                    ] as const
                  ).map(([value, labelKey]) => (
                    <button
                      key={value}
                      onClick={() => {
                        const newSet = new Set(taskStatusFilters);
                        if (newSet.has(value)) newSet.delete(value);
                        else newSet.add(value);
                        setTaskStatusFilters(
                          newSet.size > 0
                            ? newSet
                            : new Set([
                                'todo',
                                'in_progress',
                                'pending',
                                'on_hold',
                              ]),
                        );
                      }}
                      className={`px-3 py-1 rounded text-sm font-semibold transition ${
                        taskStatusFilters.has(value)
                          ? 'bg-blue-600 text-white'
                          : isDark
                            ? 'bg-slate-700 text-white'
                            : 'bg-slate-200 text-slate-900'
                      }`}
                    >
                      {t(`calendar.${labelKey}`)}
                    </button>
                  ))}
                </div>
              </div>
              {/* Time-off Status */}
              <div>
                <label
                  className={`block text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}
                >
                  {t('calendar.filterTimeOffStatus')}
                </label>
                <p
                  className={`text-xs mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                >
                  {t('calendar.filterTimeOffStatusHint')}
                </p>
                <div className="flex gap-2">
                  {(['pending', 'approved'] as const).map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        const newSet = new Set(timeoffStatusFilters);
                        if (newSet.has(status)) newSet.delete(status);
                        else newSet.add(status);
                        setTimeoffStatusFilters(newSet);
                      }}
                      className={`flex-1 px-3 py-1 rounded text-sm font-semibold transition ${
                        timeoffStatusFilters.has(status)
                          ? 'bg-blue-600 text-white'
                          : isDark
                            ? 'bg-slate-700 text-white'
                            : 'bg-slate-200 text-slate-900'
                      }`}
                    >
                      {status === 'pending'
                        ? t('calendar.filterPending')
                        : t('calendar.filterApproved')}
                    </button>
                  ))}
                </div>
              </div>
              {/* Clear Button */}
              <button
                onClick={() => {
                  setVisibleCategories(
                    new Set(['timeoff', 'entries', 'tasks']),
                  );
                  setTaskStatusFilters(
                    new Set(['todo', 'in_progress', 'pending', 'on_hold']),
                  );
                  setTimeoffStatusFilters(new Set(['pending', 'approved']));
                  setSearchParams({});
                  setFiltersOpen(false);
                }}
                className="btn btn-secondary w-full"
              >
                {t('calendar.filterClear')}
              </button>
            </div>
          </div>
        )}

        <div className={`${cardClass} rounded-2xl p-4`}>
          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {weekdayLabels.map((d) => (
              <div
                key={d}
                className={`text-center text-xs font-semibold py-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
              >
                {d}
              </div>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-72">
              <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstWeekday }).map((_, i) => (
                <div
                  key={`pad-${i}`}
                  className={`rounded-lg min-h-[96px] ${isDark ? 'bg-slate-800/30' : 'bg-slate-50'}`}
                />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const { tOff, ents, tks } = getDayData(
                  day,
                  visibleCategories,
                  taskStatusFilters,
                  timeoffStatusFilters,
                );

                const isToday =
                  year === now.getFullYear() &&
                  month === now.getMonth() + 1 &&
                  day === now.getDate();

                return (
                  <div
                    key={day}
                    className={`border rounded-lg p-1.5 min-h-[96px] flex flex-col gap-1 ${
                      isToday
                        ? isDark
                          ? 'border-violet-500/50 bg-violet-500/10'
                          : 'border-violet-300 bg-violet-50'
                        : isDark
                          ? 'border-slate-700/50 bg-slate-800/30'
                          : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div
                      className={`text-right text-xs font-bold ${
                        isToday
                          ? isDark
                            ? 'text-violet-400'
                            : 'text-violet-600'
                          : isDark
                            ? 'text-slate-400'
                            : 'text-slate-500'
                      }`}
                    >
                      {day}
                    </div>
                    {(() => {
                      const cellKey = dateKey(year, month, day);
                      const totalHoursCell = ents.reduce(
                        (sum, e) => sum + (Number(e.hours_worked) || 0),
                        0,
                      );

                      const allPills: PillItem[] = [
                        ...tOff.map((t) => ({
                          type: 'timeoff' as const,
                          data: t,
                        })),
                        ...(totalHoursCell > 0
                          ? ents
                              .slice(0, 1)
                              .map((e) => ({ type: 'entry' as const, data: e }))
                          : []),
                        ...tks.map((t) => ({ type: 'task' as const, data: t })),
                      ];

                      const isExpanded = expandedDays.has(cellKey);
                      const visiblePills = isExpanded
                        ? allPills
                        : allPills.slice(0, 3);
                      const hiddenCount = Math.max(0, allPills.length - 3);

                      return (
                        <div className="flex flex-col gap-0.5 overflow-hidden">
                          {visiblePills.map((pill, idx) => {
                            if (pill.type === 'timeoff') {
                              const to = pill.data;
                              return (
                                <span
                                  key={`to-${to.id}-${idx}`}
                                  className={`px-1.5 py-0.5 text-[10px] rounded truncate border ${to.status === 'pending' ? (isDark ? 'border-yellow-500 border-2' : 'border-yellow-400 border-2') : 'border-transparent'} ${timeOffColor[to.request_type] || (isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600')}`}
                                  title={`${to.request_type}${to.status === 'pending' ? ' (pending)' : ''}`}
                                >
                                  {to.request_type.replace('_', ' ')}
                                </span>
                              );
                            }
                            if (pill.type === 'entry') {
                              return (
                                <span
                                  key={`entry-${pill.data.id}-${idx}`}
                                  className={`px-1.5 py-0.5 text-[10px] rounded truncate ${isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}
                                  title={ents
                                    .map(
                                      (e) =>
                                        `${e.project?.name || ''}: ${roundHours(Number(e.hours_worked) || 0)}h`,
                                    )
                                    .join('\n')}
                                >
                                  {roundHours(totalHoursCell)}h{' '}
                                  {ents.length === 1 &&
                                    `${ents[0].project?.name || 'No project'}`}
                                </span>
                              );
                            }
                            const tk = pill.data;
                            return (
                              <span
                                key={`tk-${tk.id}-${idx}`}
                                className={`px-1.5 py-0.5 text-[10px] rounded truncate ${priorityColor(tk.priority)}`}
                                title={`${tk.title} — ${tk.project?.name || ''}`}
                              >
                                {tk.title}
                              </span>
                            );
                          })}
                          {hiddenCount > 0 && !isExpanded && (
                            <button
                              onClick={() =>
                                setExpandedDays((prev) => {
                                  const next = new Set(Array.from(prev));
                                  next.add(cellKey);
                                  return next;
                                })
                              }
                              className={`px-1.5 py-0.5 text-[10px] font-semibold rounded text-left ${isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                            >
                              +{hiddenCount} more
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return <DashboardLayout sections={sections}>{content}</DashboardLayout>;
};

interface LegendDotProps {
  color: string;
  label: string;
  isDark: boolean;
  isActive?: boolean;
  onClick?: () => void;
}

const LegendDot = ({
  color,
  label,
  isDark,
  isActive = true,
  onClick,
}: LegendDotProps) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 transition-opacity ${!isActive ? 'opacity-40' : 'opacity-100'} hover:opacity-80 cursor-pointer`}
  >
    <div className={`w-3 h-3 rounded-full ${color}`} />
    <span className={isDark ? 'text-white' : 'text-slate-900'}>{label}</span>
  </button>
);

export default MyCalendar;
